
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface SpecializedTutoring {
  name: string;
}

export interface TeacherSpecializedTutoring {
  id: string;
  teacher_code: string;
  specialized_tutorings: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export function useSpecializedTutorings(teacherCode: string | undefined) {
  const { toast } = useToast();
  const [availableTutorings, setAvailableTutorings] = useState<SpecializedTutoring[]>([]);
  const [teacherTutorings, setTeacherTutorings] = useState<TeacherSpecializedTutoring[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch available specialized tutorings
  const fetchAvailableTutorings = async () => {
    const { data, error } = await supabase
      .from('specialized_tutorings')
      .select('name')
      .order('name');

    if (error) {
      console.error('Error fetching specialized tutorings:', error);
    } else {
      setAvailableTutorings(data || []);
    }
  };

  // Fetch teacher's specialized tutorings
  const fetchTeacherTutorings = async () => {
    if (!teacherCode) return;

    const { data, error } = await supabase
      .from('teacher_specialized_tutorings')
      .select('*')
      .eq('teacher_code', teacherCode)
      .order('created_at');

    if (error) {
      console.error('Error fetching teacher tutorings:', error);
    } else {
      setTeacherTutorings(data || []);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([fetchAvailableTutorings(), fetchTeacherTutorings()]);
      setIsLoading(false);
    };

    fetchData();
  }, [teacherCode]);

  // Add new specialized tutoring option
  const addNewTutoringOption = async (name: string) => {
    const { error } = await supabase
      .from('specialized_tutorings')
      .insert({ name });

    if (error) {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถเพิ่มรายการติวสอบพิเศษได้',
        variant: 'destructive'
      });
      return false;
    }

    await fetchAvailableTutorings();
    toast({
      title: 'สำเร็จ',
      description: 'เพิ่มรายการติวสอบพิเศษแล้ว'
    });
    return true;
  };

  // Add tutoring for teacher
  const addTeacherTutoring = async (tutoring: string, description?: string) => {
    if (!teacherCode) return false;

    const { error } = await supabase
      .from('teacher_specialized_tutorings')
      .insert({
        teacher_code: teacherCode,
        specialized_tutorings: tutoring,
        description: description || null
      });

    if (error) {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถเพิ่มการติวสอบพิเศษได้',
        variant: 'destructive'
      });
      return false;
    }

    await fetchTeacherTutorings();
    toast({
      title: 'สำเร็จ',
      description: 'เพิ่มการติวสอบพิเศษแล้ว'
    });
    return true;
  };

  // Update teacher tutoring
  const updateTeacherTutoring = async (id: string, tutoring: string, description?: string) => {
    const { error } = await supabase
      .from('teacher_specialized_tutorings')
      .update({
        specialized_tutorings: tutoring,
        description: description || null
      })
      .eq('id', id);

    if (error) {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัพเดทการติวสอบพิเศษได้',
        variant: 'destructive'
      });
      return false;
    }

    await fetchTeacherTutorings();
    toast({
      title: 'สำเร็จ',
      description: 'อัพเดทการติวสอบพิเศษแล้ว'
    });
    return true;
  };

  // Delete teacher tutoring
  const deleteTeacherTutoring = async (id: string) => {
    const { error } = await supabase
      .from('teacher_specialized_tutorings')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถลบการติวสอบพิเศษได้',
        variant: 'destructive'
      });
      return false;
    }

    await fetchTeacherTutorings();
    toast({
      title: 'สำเร็จ',
      description: 'ลบการติวสอบพิเศษแล้ว'
    });
    return true;
  };

  return {
    availableTutorings,
    teacherTutorings,
    isLoading,
    addNewTutoringOption,
    addTeacherTutoring,
    updateTeacherTutoring,
    deleteTeacherTutoring,
    refetch: async () => {
      setIsLoading(true);
      await Promise.all([fetchAvailableTutorings(), fetchTeacherTutorings()]);
      setIsLoading(false);
    }
  };
}
